import { useState, useMemo } from "react";
import { useProperty } from "@/contexts/PropertyContext";
import { Check, Building2, Pin, PinOff } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

const PIN_KEY = "pp_pinned_properties";
const MAX_PINS = 3;

function loadPins() {
  try { return JSON.parse(localStorage.getItem(PIN_KEY) || "[]"); }
  catch { return []; }
}

export default function PropertySwitcher() {
  const { properties, propertyId, currentProperty, selectProperty } = useProperty();
  const [pins, setPins] = useState(loadPins);

  const togglePin = (e, id) => {
    e.stopPropagation();
    e.preventDefault();
    setPins((prev) => {
      const next = prev.includes(id)
        ? prev.filter((p) => p !== id)
        : [id, ...prev.slice(0, MAX_PINS - 1)];
      try { localStorage.setItem(PIN_KEY, JSON.stringify(next)); } catch (err) { if (typeof console !== 'undefined') console.debug('components/PropertySwitcher.jsx: swallowed', err); }
      return next;
    });
  };

  const { pinnedList, otherList } = useMemo(() => {
    const byId = new Map(properties.map((p) => [p.id, p]));
    const pinnedList = pins.map((id) => byId.get(id)).filter(Boolean);
    const otherList = properties
      .filter((p) => !pins.includes(p.id))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return { pinnedList, otherList };
  }, [properties, pins]);

  const renderItem = (p) => {
    const isPinned = pins.includes(p.id);
    const isActive = propertyId === p.id;
    return (
      <DropdownMenuItem
        key={p.id}
        data-testid={`switcher-option-${p.id}`}
        onClick={() => selectProperty(p.id)}
        className="flex items-start gap-2"
      >
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{p.name}</div>
          <div className="text-xs text-muted-foreground truncate">{p.type} · {p.units} units</div>
        </div>
        {isActive && <Check className="h-4 w-4 text-orange-400 mt-1 shrink-0" />}
        <button
          type="button"
          onClick={(e) => togglePin(e, p.id)}
          className={"shrink-0 mt-0.5 rounded p-1 transition-colors " +
            (isPinned ? "text-orange-400 hover:bg-orange-500/10" : "text-zinc-500 hover:text-zinc-300 hover:bg-white/5")}
          aria-label={isPinned ? `Unpin ${p.name}` : `Pin ${p.name}`}
          data-testid={`switcher-pin-${p.id}`}
        >
          {isPinned ? <Pin className="h-3 w-3 fill-orange-400" /> : <PinOff className="h-3 w-3" />}
        </button>
      </DropdownMenuItem>
    );
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="property-switcher"
          className="w-full flex items-center gap-3 rounded-md border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] px-3 py-2.5 text-left transition-colors"
        >
          <div className="h-9 w-9 rounded-md bg-orange-500/10 flex items-center justify-center border border-orange-500/30">
            <Building2 className="h-4 w-4 text-orange-400" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">Property</div>
            <div className="text-sm font-medium truncate">{currentProperty?.name || "Select property"}</div>
          </div>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-72 max-h-[70vh] overflow-y-auto" data-testid="switcher-menu">
        {pinnedList.length > 0 && (
          <>
            <DropdownMenuLabel className="text-xs uppercase tracking-widest text-orange-400 flex items-center gap-1.5" data-testid="switcher-pinned-label">
              <Pin className="h-3 w-3 fill-orange-400" /> Pinned ({pinnedList.length}/{MAX_PINS})
            </DropdownMenuLabel>
            {pinnedList.map(renderItem)}
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuLabel className="text-xs uppercase tracking-widest text-zinc-500">
          {pinnedList.length > 0 ? "All communities" : "Switch property"}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {otherList.map(renderItem)}
        {pinnedList.length === 0 && (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5 text-[10px] text-zinc-500" data-testid="switcher-pin-hint">
              Tap the pin icon to keep your top {MAX_PINS} communities at the top.
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
